import { useEditorClass } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Check as ResolveIcon } from '@tunasong/icons'
import type { CompleteEvent, EditorEvent, TunaPluginContentProps } from '@tunasong/plugin-lib'
import type { Comment, CoreElement, Entity, Persisted } from '@tunasong/schemas'
import { isPersisted } from '@tunasong/schemas'
import { Feed, ScrollContainer, IconButton, Tab, Tabs, useActionToast } from '@tunasong/ui-lib'
import { useCurrentUser } from '@tunasong/redux'
import { logger } from '@tunasong/models'
import type { FC, ReactNode } from 'react'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { Composer } from './composer.js'
import type { ComposerProps } from './composer.js'
import ChatFeed from './chat-feed.js'
import type { ChatFeedEntry, ChatFeedStandalone } from './chat-feed.js'
import { isComposerEmpty } from './composer-empty.js'
import { useChat } from './chat.hook.js'
import { ChatToastContext } from './chat-toast.js'
import { shouldPatchComment } from './should-patch-comment.js'
import cn from 'classnames'
import styles from './chat.module.css'

const ChatItemStyles = {
  fontSize: '14px !important',
  justifyContent: 'center',
}

export interface ComposerSeed {
  /** Text to hand to the composer. */
  text: string
  /** Send immediately as a comment instead of pre-filling. */
  autoSend?: boolean
  /** Monotonic hand-off id — a new hand-off always has a new seq. */
  seq: number
}

export interface ChatGroup {
  id: string
  /** ISO time of the group's latest comment */
  latestActivity: string
  content: ReactNode
}

export interface ChatProps extends TunaPluginContentProps<Entity> {
  className?: string
  showTabs?: boolean
  /** One-shot composer hand-off (pre-fill or auto-send) — see ComposerSeed. */
  composerSeed?: ComposerSeed
  /** Stable id for the composer root (aria-describedby wiring by siblings). */
  composerId?: string
  /**
   * How the composer reads and what it offers, for the agents: their placeholder (C2), the answer state with Stop,
   * the answering label (C8), a notice and the focus chip (chat makeover #2718). People Chat uses the defaults.
   */
  composerProps?: Pick<
    ComposerProps,
    'placeholder' | 'hint' | 'notice' | 'state' | 'streamingLabel' | 'onStop' | 'focusChip'
  >
  /** Shown in place of the feed once the comments have loaded and none are open. */
  emptyState?: ReactNode
  /** Offer Resolve on the user's own open comments without the tabs; the sidebar Chat does (chat makeover M3) */
  resolvable?: boolean
  /**
   * Content placed among the comments by its latest activity, newest next to the composer: the song's anchored
   * groups in the sidebar Chat (chat makeover M3, #2718). It can show the chat's toast through `useChatToast`.
   */
  groups?: ChatGroup[]
  /** Merged into each comment this chat sends: the block thread's anchor (GH #2795) */
  commentMetadata?: Record<string, unknown>
  /** Scrolling to the top asks for older groups too */
  onLoadOlder?(): void
  onComment?(comment: Comment): void
}

export const Chat: FC<ChatProps> = props => {
  const {
    element,
    className,
    onComment,
    showTabs = true,
    resolvable = showTabs,
    composerSeed,
    composerId,
    emptyState,
    composerProps,
    groups = [],
    onLoadOlder,
    commentMetadata,
  } = props
  const updateEntity = graphHooks.useEntityUpdate()

  const ChatEditor = useEditorClass('ChatEditor')

  const { userId } = useCurrentUser()

  const { activeComments, resolvedComments, addComment, resolveComment, reopenComment, getNext, isLoading } = useChat({
    element,
    pageSize: 10,
    commentMetadata,
  })
  /** Resolving hides the comment from the thread, so it offers Undo (reversible actions act at once and offer Undo) */
  const { showToast, toast } = useActionToast()
  const handleResolve = (c: Persisted<Comment>) => () => {
    void resolveComment(c)
    showToast({
      message: 'Comment resolved.',
      actionLabel: 'Undo',
      onAction: () => void reopenComment(c),
    })
  }

  /**
   * A failed send keeps its text and offers Retry (GH #2453; Joni's copy). It shares the resolve toast above: one
   * toast at a time, newest wins.
   */
  const showSendFailed = (retry: () => void, { restored }: { restored: boolean }) =>
    showToast({
      message: restored ? "Couldn't send your comment. It's back in the box." : "Couldn't send your comment.",
      actionLabel: 'Retry',
      onAction: retry,
      severity: 'error',
      /** With the text back in the box, Send is a second way back; otherwise Retry is the only one, so it stays */
      duration: restored ? undefined : null,
    })
  /** The auto-send effect must not re-run because the toast's callbacks change identity */
  const handleAutoSendFailed = useEffectEvent((retry: () => void) => showSendFailed(retry, { restored: false }))

  const commentTemplate = element?.id
    ? ({
        type: 'comment',
        parentId: element.id,
        userId: userId ?? 'unknown',
        children: [{ type: 'line', children: [{ text: '' }] }],
      } satisfies CoreElement<Comment>)
    : null

  const commentRef = useRef<Comment | null>(commentTemplate)

  /**
   * Auto-send hand-off: post `composerSeed.text` as a comment immediately and
   * fire `onComment`. Keyed on `seq` so each hand-off fires exactly once even
   * though `commentTemplate`/`addComment` change identity across renders.
   */
  const autoSubmittedSeq = useRef(0)
  useEffect(() => {
    if (!composerSeed?.autoSend || composerSeed.text.length === 0 || !commentTemplate) {
      return
    }
    if (autoSubmittedSeq.current === composerSeed.seq) {
      return
    }
    autoSubmittedSeq.current = composerSeed.seq
    const comment: Comment = {
      ...commentTemplate,
      children: [{ type: 'line', children: [{ text: composerSeed.text }] }],
    }
    const send = async () => {
      try {
        const created = await addComment(comment)
        if (onComment && created) {
          onComment(created)
        }
      } catch (error) {
        logger.warn('Comment send failed', { parentId: comment.parentId, error })
        handleAutoSendFailed(() => void send())
      }
    }
    void send()
  }, [composerSeed, commentTemplate, addComment, onComment])

  /**
   * GH #1280: gate PATCH on a real content edit. Plate's onValueChange
   * fires on initial normalization and on every prop-driven editor
   * re-create, which previously produced a phantom PATCH on every
   * rendered comment — and a 404-racing PATCH against the just-POSTed
   * comment. Updates may legitimately occur even when the editor is
   * readOnly (e.g. todo checkboxes nested inside the comment), so the
   * decision is content-based, not readOnly-based.
   */
  const handleCommentUpdate = (original: Persisted<Comment>) => (e: CoreElement) => {
    if (!shouldPatchComment(original, e)) {
      return
    }
    updateEntity(e.id, e)
  }

  const createFeedItems = (feedComments: Persisted<Comment>[] = []) =>
    feedComments?.map(c => ({
      ...c,
      content: (
        <>
          <div className={styles.feedItemContent}>
            {/* @todo this will flicker when receiving updates. Can we render this offline? */}
            <ChatEditor
              style={ChatItemStyles}
              readOnly={true}
              autoFocus={false}
              element={c}
              onChange={handleCommentUpdate(c)}
            />
          </div>
        </>
      ),
      actions:
        resolvable && isPersisted(c) && !c.resolved && c.userId === userId ? (
          <IconButton className={styles.resolveButton} aria-label="Resolve comment" onClick={handleResolve(c)}>
            <ResolveIcon />
          </IconButton>
        ) : undefined,
    })) ?? []

  /**
   * comment state is set to the comment object
   */
  /** We keep a counter, so that we ignore pending comments when the expected count is at what we expect */
  const expectedCommentCount = useRef(0)

  /** Send a comment that failed while the box held something else; the box is never touched */
  const sendFailedComment = async (failed: Comment) => {
    try {
      const created = await addComment(failed)
      if (onComment && created) {
        onComment(created)
      }
    } catch (error) {
      logger.warn('Comment send failed', { parentId: failed.parentId, error })
      showSendFailed(() => void sendFailedComment(failed), { restored: false })
    }
  }

  /** Resolves false when the send failed, so the composer puts the text back (GH #2453) */
  const handleAddComment = async (ev: EditorEvent, retry: () => Promise<void>): Promise<boolean> => {
    ev.preventDefault()
    const comment = commentRef.current
    invariant(comment, 'Comment should be set')

    expectedCommentCount.current = activeComments.length + 1

    /** @todo The problem here is that the update from server side comes before this code is executed */
    let createdComment: Persisted<Comment> | undefined
    try {
      createdComment = await addComment(comment)
    } catch (error) {
      logger.warn('Comment send failed', { parentId: comment.parentId, error })
      const { editor } = (ev.detail ?? {}) as Partial<CompleteEvent>
      const restored = Boolean(editor && isComposerEmpty(editor))
      /**
       * Restored: the box holds this comment again, so Retry submits the box. Not restored: the box holds a new
       * draft, so Retry must send exactly the comment that failed and leave the draft alone (GH #2453, Joni's P1).
       */
      showSendFailed(restored ? () => void retry() : () => void sendFailedComment(comment), { restored })
      return false
    }

    if (onComment && createdComment) {
      onComment(createdComment)
    }
    commentRef.current = commentTemplate
    return true
  }

  const comments = createFeedItems(activeComments)
  /** Groups sit among the comments by their latest activity (chat makeover M3); with none, the order is unchanged */
  const feed: ChatFeedEntry[] =
    groups.length === 0
      ? comments
      : [
          ...comments.map(item => ({ item, at: item.createdAt })),
          ...groups.map(group => ({
            item: {
              id: group.id,
              createdAt: group.latestActivity,
              content: group.content,
              standalone: true as const,
            } satisfies ChatFeedStandalone,
            at: group.latestActivity,
          })),
        ]
          .sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''))
          .map(({ item }) => item)
  const resolved = createFeedItems(resolvedComments)

  const handleComment = (c: Comment) => (commentRef.current = c)

  const handleUp = async () => {
    onLoadOlder?.()
    await getNext()
  }

  const [tab, setTab] = useState(0)
  const handleTab = (_ev: React.SyntheticEvent | null, num: string | number) => setTab(num as number)
  // we use state to trigger a re-render since we need topRef to be set for ScrollContainer
  const [topEl, setTopEl] = useState<HTMLDivElement | null>(null)

  return (
    <div className={cn(styles.container, className)}>
      {showTabs && (
        <Tabs value={tab} onChange={handleTab}>
          <Tab label={`Active`} />
          <Tab label={`Resolved (${resolved.length})`} />
        </Tabs>
      )}

      {tab === 0 && (
        <>
          {emptyState && !isLoading && feed.length === 0 ? (
            <div className={styles.empty}>{emptyState}</div>
          ) : (
            <ScrollContainer onUp={handleUp} topEl={topEl}>
              <ChatToastContext value={showToast}>
                <ChatFeed items={feed} topRef={setTopEl} />
              </ChatToastContext>
            </ScrollContainer>
          )}
          {commentRef.current ? (
            <Composer
              /** C12; the agents pass their own (C2) */
              placeholder="Write a comment…"
              {...composerProps}
              onChange={handleComment}
              onComplete={handleAddComment}
              seed={
                composerSeed && !composerSeed.autoSend ? { text: composerSeed.text, seq: composerSeed.seq } : undefined
              }
              id={composerId}
              aria-describedby={composerId ? `${composerId}-role-description` : undefined}
            />
          ) : null}
        </>
      )}

      {tab === 1 ? <Feed items={resolved} className={styles.resolvedFeed} /> : null}
      {toast}
    </div>
  )
}
